import { TextField } from "@/components/ui/TextField";
import { Button } from "@/components/ui/Button";
import { PucTab } from "@/components/features/PucTab";
import type { Usuario } from "@/api/usuarios";
import type { EmpresaUsuario } from "@/api/empresas";

export type EmpresaTabId =
  | "info"
  | "contacto"
  | "notas"
  | "scanner"
  | "miembros"
  | "puc-compras"
  | "puc-ventas"
  | "puc-exterior"
  | "puc-legalizacion";

export interface EmpresaTabsState {
  razonSocial: string;
  setRazonSocial: (v: string) => void;
  nit: string;
  setNit: (v: string) => void;
  activa: boolean;
  setActiva: (v: boolean) => void;
  direccion: string;
  setDireccion: (v: string) => void;
  telefono: string;
  setTelefono: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  persona: string;
  setPersona: (v: string) => void;
  observaciones: string;
  setObservaciones: (v: string) => void;
  modeloIa: string;
  setModeloIa: (v: string) => void;
  temperature: string;
  setTemperature: (v: string) => void;
  umbral: string;
  setUmbral: (v: string) => void;
  heuristica: boolean;
  setHeuristica: (v: boolean) => void;
  selectedUsers: Map<number, EmpresaUsuario["rol_empresa"]>;
  setSelectedUsers: React.Dispatch<
    React.SetStateAction<Map<number, EmpresaUsuario["rol_empresa"]>>
  >;
  pendingUserId: string;
  setPendingUserId: (v: string) => void;
  allUsuarios: Usuario[];
  addMember: () => void;
  removeMember: (userId: number) => void;
  setMemberRole: (userId: number, rol: EmpresaUsuario["rol_empresa"]) => void;
  isEdit: boolean;
  empresaId: number | null;
}

interface EmpresaTabsContentProps {
  activeTab: EmpresaTabId;
  state: EmpresaTabsState;
}

const InfoTab = (state: EmpresaTabsState) => (
  <div className="space-y-3">
    <TextField
      label="Razón social"
      name="razonSocial"
      required
      value={state.razonSocial}
      onChange={(e) => state.setRazonSocial(e.target.value)}
    />
    <TextField
      label="NIT"
      name="nit"
      required
      value={state.nit}
      onChange={(e) => state.setNit(e.target.value)}
      hint="Formato 900123456-7"
    />
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={state.activa}
        onChange={(e) => state.setActiva(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      <span>Empresa activa</span>
    </label>
  </div>
);

const ContactoTab = (state: EmpresaTabsState) => (
  <div className="space-y-3">
    <TextField
      label="Dirección"
      name="direccion"
      value={state.direccion}
      onChange={(e) => state.setDireccion(e.target.value)}
    />
    <div className="grid gap-3 sm:grid-cols-2">
      <TextField
        label="Teléfono"
        name="telefono"
        value={state.telefono}
        onChange={(e) => state.setTelefono(e.target.value)}
      />
      <TextField
        label="Email de contacto"
        name="email"
        type="email"
        value={state.email}
        onChange={(e) => state.setEmail(e.target.value)}
      />
    </div>
    <TextField
      label="Persona de contacto"
      name="persona"
      value={state.persona}
      onChange={(e) => state.setPersona(e.target.value)}
    />
  </div>
);

const NotasTab = (state: EmpresaTabsState) => (
  <TextField
    as="textarea"
    label="Observaciones"
    name="observaciones"
    value={state.observaciones}
    onChange={(e) => state.setObservaciones(e.target.value)}
    rows={6}
  />
);

const ScannerTab = (state: EmpresaTabsState) => (
  <div className="space-y-3">
    <TextField
      label="Modelo de IA"
      name="modeloIa"
      hint="Recomendado: gemini-2.5-flash"
      value={state.modeloIa}
      onChange={(e) => state.setModeloIa(e.target.value)}
    />
    <div className="grid gap-3 sm:grid-cols-2">
      <TextField
        label="Temperatura"
        name="temperature"
        hint="0.0 (determinista) a 1.0 (creativo)"
        value={state.temperature}
        onChange={(e) => state.setTemperature(e.target.value)}
      />
      <TextField
        label="Umbral de confianza"
        name="umbral"
        hint="0.0 a 1.0 (recomendado 0.7)"
        value={state.umbral}
        onChange={(e) => state.setUmbral(e.target.value)}
      />
    </div>
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={state.heuristica}
        onChange={(e) => state.setHeuristica(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      <span>Activar heurística de validación</span>
    </label>
  </div>
);

const MiembrosTab = (state: EmpresaTabsState) => (
  <div className="space-y-3">
    <div className="flex gap-2">
      <select
        value={state.pendingUserId}
        onChange={(e) => state.setPendingUserId(e.target.value)}
        className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
      >
        <option value="">Selecciona un usuario</option>
        {state.allUsuarios
          .filter((u) => !state.selectedUsers.has(u.id))
          .map((u) => (
            <option key={u.id} value={u.id}>
              {u.nombre} ({u.email})
            </option>
          ))}
      </select>
      <Button onClick={state.addMember} disabled={!state.pendingUserId}>
        Agregar
      </Button>
    </div>
    {state.selectedUsers.size > 0 ? (
      <ul className="space-y-2">
        {Array.from<[number, string]>(state.selectedUsers.entries()).map(([userId, rol]) => {
          const u = state.allUsuarios.find((x: Usuario) => x.id === userId);
          return (
            <li
              key={userId}
              className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3"
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-ink-primary">
                  {u?.nombre ?? `Usuario #${userId}`}
                </p>
                <p className="text-xs text-ink-secondary">{u?.email}</p>
              </div>
              <select
                value={rol}
                onChange={(e) =>
                  state.setMemberRole(
                    userId,
                    e.target.value as EmpresaUsuario["rol_empresa"],
                  )
                }
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm"
              >
                <option value="admin_empresa">Admin</option>
                <option value="operador">Operador</option>
                <option value="lector">Lector</option>
              </select>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => state.removeMember(userId)}
              >
                Quitar
              </Button>
            </li>
          );
        })}
      </ul>
    ) : null}
  </div>
);

export const EmpresaTabsContent = ({
  activeTab,
  state,
}: EmpresaTabsContentProps) => {
  if (activeTab === "info") return <InfoTab {...state} />;
  if (activeTab === "contacto") return <ContactoTab {...state} />;
  if (activeTab === "notas") return <NotasTab {...state} />;
  if (activeTab === "scanner") return <ScannerTab {...state} />;
  if (activeTab === "miembros") return <MiembrosTab {...state} />;

  if (activeTab.startsWith("puc-") && state.empresaId) {
    const tipo =
      activeTab === "puc-compras"
        ? ""
        : activeTab === "puc-ventas"
        ? "_ventas"
        : activeTab === "puc-exterior"
        ? "_exterior"
        : "_legalizacion";
    const title =
      activeTab === "puc-compras"
        ? "PUC — Compras"
        : activeTab === "puc-ventas"
        ? "PUC — Ventas"
        : activeTab === "puc-exterior"
        ? "PUC — Exterior"
        : "PUC — Legalización";
    return <PucTab empresaId={state.empresaId} tipo={tipo} title={title} />;
  }

  if (!state.isEdit) {
    return (
      <p className="text-sm text-ink-secondary">
        Guarda la empresa primero para poder configurar esta sección.
      </p>
    );
  }

  return null;
};